import React, { ReactElement, ReactNode } from 'react';
import Helmet, { HelmetProps } from 'react-helmet';
import { Row, Col } from 'react-bootstrap';
import { graphql, StaticQuery } from 'gatsby';
import { WindowLocation } from '@reach/router';

import { ErrorBoundary, Headline, Morph } from 'src/components/Common';
import { Project as ProjectNavigation } from 'src/components/Navigation';

import Footer from './Partials/_Footer';
import Header from './Partials/_Header';
import RemovePreloader from './Partials/_RemovePreloader';

interface ProjectLayoutProps {
  /** layout content */
  children: ReactNode;
  /** location prop from page */
  location: WindowLocation<unknown>;
}

const ProjectLayout = ({
  children,
  location,
}: ProjectLayoutProps): ReactElement => (
  <ErrorBoundary>
    <StaticQuery
      query={graphql`
        query ProjectLayoutQuery {
          site {
            buildTime
            siteMetadata {
              buildVersion
              title
              packageVersion
            }
          }
          # used to get current page data through location
          allPages: allSitePage {
            edges {
              node {
                path
                pageContext
              }
            }
          }
          projectPages: allSitePage(
            filter: { path: { regex: "/^/projekte/.*/$/" } }
          ) {
            edges {
              node {
                path
                pageContext
              }
            }
          }
        }
      `}
      render={(data) => {
        const menuPages = data.allPages.edges
          .filter(({ node }) => node.pageContext.frontmatter?.showInMenu)
          .sort((a, b) =>
            a.node.pageContext.frontmatter.sorting >
            b.node.pageContext.frontmatter.sorting
              ? 1
              : -1,
          )
          .map(({ node }) => ({
            path: node.path,
            title: node.pageContext.frontmatter?.title || 'no title',
          }));

        let thisPage;
        try {
          thisPage = data.allPages.edges.find(
            ({ node }) => node.path === location.pathname,
          ).node;
        } catch (e) {
          thisPage = data.allPages.edges.find(
            ({ node }) => node.path === '/404/',
          ).node;
        }

        let nextProject;
        let prevProject;
        if (thisPage) {
          const thisProjectIndex = data.projectPages.edges.findIndex(
            ({ node }) => node.path === location.pathname,
          );
          let nextProjectIndex = thisProjectIndex + 1;
          let prevProjectIndex = thisProjectIndex - 1;
          if (thisProjectIndex === 0) {
            prevProjectIndex = data.projectPages.edges.length - 1;
          } else if (thisProjectIndex === data.projectPages.edges.length - 1) {
            nextProjectIndex = 0;
          }
          nextProject = {
            path: data.projectPages.edges[nextProjectIndex].node.path,
            title:
              data.projectPages.edges[nextProjectIndex].node.pageContext
                .frontmatter.title,
          };
          prevProject = {
            path: data.projectPages.edges[prevProjectIndex].node.path,
            title:
              data.projectPages.edges[prevProjectIndex].node.pageContext
                .frontmatter.title,
          };
        }

        // #region helmetProps sammeln
        // TODO: Add open graph
        // Metadaten generieren
        const metadata = [];
        if (thisPage.pageContext.frontmatter.metaDescription) {
          metadata.push({
            name: 'description',
            content: thisPage.pageContext.frontmatter.metaDescription,
          });
        }

        const helmetProps: HelmetProps = {
          htmlAttributes: {
            lang: 'de',
          },
          meta: metadata,
        };

        // Seitentitel generieren
        if (thisPage.pageContext.frontmatter.browserTitle) {
          helmetProps.title = thisPage.pageContext.frontmatter.browserTitle;
        } else {
          helmetProps.title = thisPage.pageContext.frontmatter.title;
          helmetProps.titleTemplate = `%s | ${data.site.siteMetadata.title}`;
        }
        // #endregion

        return (
          <div
            className={`layout-project layout-color-${thisPage.pageContext.frontmatter.layoutColor}`}
          >
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Helmet {...helmetProps} />
            {/* try to remove preloader on every update */}
            <RemovePreloader update={Math.random()} />
            <Header
              location={location}
              pages={menuPages}
              siteTitle={data.site.siteMetadata.title}
              styleInverted
            />
            <Morph />
            <header className="container mb-8">
              <Row>
                <Col md={{ span: 6, offset: 6 }}>
                  <Headline size={1}>
                    {thisPage.pageContext.frontmatter.name}
                    <br />
                    <small>{thisPage.pageContext.frontmatter.type}</small>
                  </Headline>
                </Col>
              </Row>
            </header>
            <section id="main-content" className="relative">
              {children}
            </section>
            {nextProject && prevProject && (
              <ProjectNavigation
                nextProject={nextProject}
                prevProject={prevProject}
              />
            )}
            <Footer
              siteBuildTime={data.site.buildTime}
              siteBuildVersion={data.site.siteMetadata.buildVersion}
              sitePackageVersion={data.site.siteMetadata.packageVersion}
              siteTitle={data.site.siteMetadata.title}
            />
          </div>
        );
      }}
    />
  </ErrorBoundary>
);

export default ProjectLayout;
